import { useQuery } from '@apollo/client'
import isEmpty from 'lodash-es/isEmpty'
import React, { useMemo } from 'react'

import { Dialog, LoadingSpinner } from '@syconium/little-miss-figgy/dist/components'

import { gql } from '../../../../__generated__/graphql/catalog'
import { FetchColorGalleryQuery } from '../../../../__generated__/graphql/catalog/graphql'
import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'

import GalleryTile from './ColorGalleryTile'
import * as Styles from './styles'

type ColorGalleryModalProps = {
  productGroupHandle: string
  colorCount: number
}

const ColorGalleryModalContent = ({ productGroupHandle }: ColorGalleryModalProps) => {
  const {
    colorGallery: { colors },
  } = useTranslation()

  // TODO: This should have paging. We should not be loading products in bulk.
  const { data, loading } = useQuery(
    gql(`
      query FetchColorGallery($handle: String!) {
        productCatalogStandardProduct(handle: $handle) {
          handle
          variantGroups(first: 48) {
            nodes {
              id
              __typename
              urlPath
              images(first: 1) {
                url
              }
              color {
                id
                displayValue
                group {
                  id
                  displayValue
                }
              }
            }
          }
        }
      }
      `),
    {
      skip: !productGroupHandle,
      variables: { handle: productGroupHandle },
      onError: error =>
        reportClientError({
          error,
          context: { scope: 'Color Gallery Modal' },
        }),
    }
  )

  const colorGroups = useMemo(() => {
    const variantGroups = data?.productCatalogStandardProduct.variantGroups
    if (isEmpty(variantGroups)) return []

    const colorGroups: Record<
      string,
      {
        group: {
          id: string
          displayValue: string
        }
        variantGroups: Array<
          FetchColorGalleryQuery['productCatalogStandardProduct']['variantGroups']['nodes'][number]
        >
      }
    > = {}
    variantGroups.nodes.forEach(variantGroup => {
      if (variantGroup.color?.group) {
        const colorGroup = colorGroups[variantGroup.color.group.id] ?? {
          group: variantGroup.color.group,
          variantGroups: [],
        }
        colorGroup.variantGroups.push(variantGroup)
        colorGroups[variantGroup.color.group.id] = colorGroup
      }
    })
    return Object.values(colorGroups)
  }, [data?.productCatalogStandardProduct.variantGroups])

  return (
    <>
      {loading ? (
        <Styles.LoadingSpinnerWrapper>
          <LoadingSpinner label='Loading colors…' size='100px' />
        </Styles.LoadingSpinnerWrapper>
      ) : (
        <Styles.DialogContent>
          {colorGroups.map(colorGroup => (
            <React.Fragment key={colorGroup.group.id}>
              <Styles.DialogTitle>
                {colorGroup.group.displayValue} {colors}
              </Styles.DialogTitle>
              <Styles.GalleryWrap>
                {colorGroup.variantGroups.map(variantGroup => {
                  if (!variantGroup.color || !variantGroup.images?.[0]?.url) return null
                  return (
                    <GalleryTile
                      key={variantGroup.id}
                      colorDisplayValue={variantGroup.color.displayValue}
                      colorKey={variantGroup.color.id}
                      pdpUrl={variantGroup.urlPath}
                      imageUrl={variantGroup.images?.[0]?.url}
                    />
                  )
                })}
              </Styles.GalleryWrap>
            </React.Fragment>
          ))}
        </Styles.DialogContent>
      )}
    </>
  )
}

const ColorGalleryModal = (props: ColorGalleryModalProps) => {
  const {
    colorGallery: { colors, title },
  } = useTranslation()

  return (
    <Styles.DialogModal
      fullscreenMobile
      unmountOnHide={true}
      header={
        <Dialog.Header
          title={
            <Styles.ModalTitleWrap>
              <Dialog.Title align='left'>
                <Styles.DialogHeader>{title}</Styles.DialogHeader>
              </Dialog.Title>
              {props.colorCount && (
                <Styles.ColorCount>
                  {props.colorCount} {colors}
                </Styles.ColorCount>
              )}
            </Styles.ModalTitleWrap>
          }
          dismiss={<Dialog.Dismiss aria-label='Close modal' />}
        />
      }
      footer={null}
      content={<ColorGalleryModalContent {...props} />}
    ></Styles.DialogModal>
  )
}

export default ColorGalleryModal
